import { useInfiniteQuery, type InfiniteData, type UseInfiniteQueryResult } from '@tanstack/react-query';
import {
  fetchControlProcessFilterOptionsPage,
  looksLikeUuid,
  resolveWireRiskId,
} from '../../request/controls/controlRiskMappings.ts';

export const controlProcessWithRiskMappingQueryKey = (riskId: string, search: string, auditYearId?: string | null) =>
  ['controls', 'process-with-risk-mapping-options', riskId, search, auditYearId ?? null] as const;

export type ControlProcessFilterOptionsPageView = Awaited<
  ReturnType<typeof fetchControlProcessFilterOptionsPage>
>;

export function useControlProcessFilterOptions(
  search: string,
  riskId: string,
  riskMappings: Array<Record<string, unknown>> = [],
  enabled = true,
  riskOptions: Array<{ value: string; label?: string; riskCode?: string }> = [],
  auditYearId?: string | null
): UseInfiniteQueryResult<InfiniteData<ControlProcessFilterOptionsPageView>, Error> {
  const resolvedRiskId = resolveWireRiskId(riskId, { riskMappings, riskOptions });
  const apiRiskId = looksLikeUuid(resolvedRiskId) ? resolvedRiskId : '';

  return useInfiniteQuery({
    queryKey: controlProcessWithRiskMappingQueryKey(apiRiskId, search, auditYearId),
    enabled: enabled && Boolean(apiRiskId),
    staleTime: 0,
    refetchOnMount: 'always',
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      fetchControlProcessFilterOptionsPage({
        page: Number(pageParam) || 1,
        pageSize: 20,
        search,
        riskId: apiRiskId,
        auditYearId: auditYearId ?? undefined,
      }),
    getNextPageParam: (lastPage) => {
      const items = lastPage.items;
      if (!Array.isArray(items) || items.length === 0) return undefined;
      const pageSize = lastPage.pageSize || 20;
      if (lastPage.isLastPage === true) return undefined;
      if (lastPage.isLastPage == null && items.length < pageSize) return undefined;
      const page = typeof lastPage.page === 'number' && Number.isFinite(lastPage.page) ? lastPage.page : 1;
      return page + 1;
    },
  });
}
